import React, { useState } from 'react';
import { GLLineItem, GLAccount, Job } from '../types';

interface Props {
  lines: GLLineItem[];
  glAccounts: GLAccount[];
  jobs: Job[];
  onChange: (lines: GLLineItem[]) => void;
  readOnly?: boolean;
}

function JobSelect({ value, jobs, onChange }: { value: string | null; jobs: Job[]; onChange: (val: string) => void }) {
  const [open, setOpen] = useState(false);
  const selected = jobs.find(j => j.job_number === value);

  return (
    <div style={{ position: 'relative' }}>
      <select
        value={value ?? ''}
        onChange={e => onChange(e.target.value)}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
        style={{
          padding: '6px 8px', border: '1px solid #cbd5e0', borderRadius: '4px',
          fontSize: '13px', width: '100%', color: '#1a202c',
          backgroundColor: 'white', outline: 'none',
        }}
      >
        <option value="">Select Job</option>
        {jobs.map(j => (
          <option key={j.job_number} value={j.job_number}>
            {j.job_number} — {j.job_name}
          </option>
        ))}
      </select>
      {!open && selected && (
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
          display: 'flex', alignItems: 'center',
          padding: '6px 24px 6px 8px', fontSize: '13px', color: '#1a202c',
          pointerEvents: 'none', backgroundColor: 'white',
          borderRadius: '4px', border: '1px solid #cbd5e0',
          overflow: 'hidden', whiteSpace: 'nowrap',
        }}>
          {selected.job_number}
        </div>
      )}
    </div>
  );
}

function GLLineItems({ lines, glAccounts, jobs, onChange, readOnly }: Props) {
  const [confirmIndex, setConfirmIndex] = useState<number | null>(null);
  const [amountInputs, setAmountInputs] = useState<{ [key: number]: string }>({});

  const updateLine = (index: number, field: keyof GLLineItem, value: string | number) => {
    onChange(lines.map((line, i) => i === index ? { ...line, [field]: value } : line));
  };

  const addLine = () => {
    onChange([...lines, { description: '', amount: 0, glAccountNumber: '', jobNumber: '' }]);
  };

  const removeLine = () => {
    if (confirmIndex === null) return;
    onChange(lines.filter((_, i) => i !== confirmIndex));
    setConfirmIndex(null);
  };

  const handleAmountChange = (index: number, value: string) => {
    setAmountInputs(prev => ({ ...prev, [index]: value.replace(/[^0-9.]/g, '') }));
  };

  const handleAmountBlur = (index: number) => {
    const raw = amountInputs[index];
    if (raw !== undefined) {
      updateLine(index, 'amount', parseFloat(raw) || 0);
      setAmountInputs(prev => { const n = { ...prev }; delete n[index]; return n; });
    }
  };

  const getAmountDisplay = (index: number, amount: number) =>
    amountInputs[index] !== undefined ? amountInputs[index] : Number(amount).toFixed(2);

  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '6px 8px', border: '1px solid #cbd5e0',
    borderRadius: '4px', fontSize: '13px', boxSizing: 'border-box',
    color: '#1a202c', outline: 'none', fontFamily: 'inherit',
  };

  return (
    <div style={{
      backgroundColor: 'white', borderRadius: '8px', padding: '24px',
      marginBottom: '16px', boxShadow: '0 1px 4px rgba(0,56,108,0.08)',
    }}>
      <h2 style={{
        margin: '0 0 16px 0', fontSize: '14px', color: '#00386C',
        textTransform: 'uppercase', letterSpacing: '0.6px', fontWeight: 700,
      }}>
        GL Line Items
      </h2>

      {/* Confirm Delete Dialog */}
      {confirmIndex !== null && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.45)', zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '8px', padding: '32px',
            maxWidth: '400px', width: '90%', boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            fontFamily: 'inherit', textAlign: 'center',
            borderTop: '4px solid #E33126',
          }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>🗑️</div>
            <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', color: '#00386C' }}>Remove Line Item?</h3>
            <p style={{ color: '#4a5568', marginBottom: '24px', fontSize: '14px' }}>
              Are you sure you want to remove line {confirmIndex + 1}
              {lines[confirmIndex]?.description ? ` (${lines[confirmIndex].description})` : ''}?
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={() => setConfirmIndex(null)}
                style={{
                  padding: '8px 24px', border: '1px solid #cbd5e0', borderRadius: '4px',
                  backgroundColor: 'white', cursor: 'pointer', fontSize: '14px', color: '#4a5568',
                  fontFamily: 'inherit',
                }}
              >
                Cancel
              </button>
              <button
                onClick={removeLine}
                style={{
                  padding: '8px 24px', border: 'none', borderRadius: '4px',
                  backgroundColor: '#E33126', cursor: 'pointer', fontSize: '14px',
                  color: 'white', fontFamily: 'inherit', fontWeight: 700,
                }}
              >
                Remove
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', tableLayout: 'fixed', minWidth: '560px' }}>
          <colgroup>
            {!readOnly && <col style={{ width: '5%' }} />}
            <col style={{ width: readOnly ? '33%' : '30%' }} />
            <col style={{ width: readOnly ? '30%' : '28%' }} />
            <col style={{ width: readOnly ? '17%' : '15%' }} />
            <col style={{ width: readOnly ? '20%' : '15%' }} />
            {!readOnly && <col style={{ width: '7%' }} />}
          </colgroup>
          <thead>
            <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
              {!readOnly && <th style={{ padding: '8px' }}></th>}
              {['Description', 'GL Account', 'Job', 'Amount', ...(readOnly ? [] : [''])].map((h, i) => (
                <th key={i} style={{
                  textAlign: i === 3 ? 'right' : 'left',
                  padding: '8px', color: '#718096', fontWeight: 600,
                  fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px',
                }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {lines.map((line, index) => (
              <tr key={index} style={{
                borderBottom: '1px solid #f7fafc',
                backgroundColor: index % 2 === 0 ? 'white' : '#fafbfc',
              }}>
                {!readOnly && (
                  <td style={{ padding: '8px', textAlign: 'center' }}>
                    <button
                      onClick={() => setConfirmIndex(index)}
                      title="Remove this line"
                      style={{
                        width: 22, height: 22, padding: 0, fontSize: '12px',
                        border: '1px solid #fed7d7', borderRadius: '3px',
                        backgroundColor: '#fff5f5', color: '#E33126',
                        cursor: 'pointer', lineHeight: 1, display: 'inline-flex',
                        alignItems: 'center', justifyContent: 'center',
                      }}
                    >
                      ✕
                    </button>
                  </td>
                )}
                <td style={{ padding: '8px' }}>
                  {readOnly ? (
                    <span style={{ fontSize: '13px', color: '#1a202c' }}>{line.description}</span>
                  ) : (
                    <input
                      type="text"
                      value={line.description}
                      onChange={e => updateLine(index, 'description', e.target.value)}
                      style={inputStyle}
                    />
                  )}
                </td>
                <td style={{ padding: '8px' }}>
                  {readOnly ? (
                    <span style={{ fontSize: '13px', color: '#1a202c' }}>
                      {glAccounts.find(a => a.gl_number === line.glAccountNumber)
                        ? `${line.glAccountNumber} — ${glAccounts.find(a => a.gl_number === line.glAccountNumber)!.description}`
                        : line.glAccountNumber}
                    </span>
                  ) : (
                    <select
                      value={line.glAccountNumber}
                      onChange={e => updateLine(index, 'glAccountNumber', e.target.value)}
                      style={{ ...inputStyle, padding: '6px 8px' }}
                    >
                      <option value="">Select GL Account</option>
                      {glAccounts.map(a => (
                        <option key={a.gl_number} value={a.gl_number}>
                          {a.gl_number} — {a.description}
                        </option>
                      ))}
                    </select>
                  )}
                </td>
                <td style={{ padding: '8px' }}>
                  {readOnly ? (
                    <span style={{ fontSize: '13px', color: line.jobNumber ? '#1a202c' : '#a0aec0' }}>
                      {line.jobNumber || '—'}
                    </span>
                  ) : (
                    <JobSelect
                      value={line.jobNumber}
                      jobs={jobs}
                      onChange={val => updateLine(index, 'jobNumber', val)}
                    />
                  )}
                </td>
                <td style={{ padding: '8px' }}>
                  {readOnly ? (
                    <span style={{ fontSize: '13px', color: '#1a202c', display: 'block', textAlign: 'right' }}>
                      ${Number(line.amount).toFixed(2)}
                    </span>
                  ) : (
                    <div style={{
                      display: 'flex', alignItems: 'center', border: '1px solid #cbd5e0',
                      borderRadius: '4px', overflow: 'hidden',
                    }}>
                      <span style={{
                        padding: '6px 6px 6px 8px', backgroundColor: '#f0f4f8',
                        color: '#718096', fontSize: '13px', borderRight: '1px solid #cbd5e0',
                        flexShrink: 0,
                      }}>$</span>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={getAmountDisplay(index, line.amount)}
                        onChange={e => handleAmountChange(index, e.target.value)}
                        onBlur={() => handleAmountBlur(index)}
                        style={{
                          width: '100%', padding: '6px 8px', border: 'none',
                          fontSize: '13px', outline: 'none', textAlign: 'right',
                          fontFamily: 'inherit', color: '#1a202c', backgroundColor: 'white',
                        }}
                      />
                    </div>
                  )}
                </td>
                {!readOnly && <td style={{ padding: '8px' }}></td>}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {!readOnly && (
        <button
          onClick={addLine}
          style={{
            marginTop: '12px', padding: '6px 12px',
            background: 'none', border: '1px dashed #00386C',
            cursor: 'pointer', fontSize: '13px', color: '#00386C',
            fontFamily: 'inherit', borderRadius: '4px', fontWeight: 600,
          }}
        >
          + Add Line
        </button>
      )}
    </div>
  );
}

export default GLLineItems;
